export interface CommonBackendDegradationEnvelope {
  code: string;
  requestId?: string;
  workspaceId?: string;
  retryable?: boolean;
  userVisibleSeverity?: 'silent' | 'inline' | 'blocking';
  diagnosticClass?: string;
  diagnosticRef?: string;
}

export class CloudBackendDegradationError extends Error {
  override readonly name = 'CloudBackendDegradationError';

  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly degradation?: CommonBackendDegradationEnvelope
  ) {
    super(message);
  }
}

export function isCloudBackendDegradationError(
  error: unknown
): error is CloudBackendDegradationError {
  return error instanceof CloudBackendDegradationError;
}

export function readBackendDegradationFromBody(
  body: unknown
): CommonBackendDegradationEnvelope | undefined {
  if (!isRecord(body)) return undefined;
  const error = isRecord(body.error) ? body.error : undefined;
  const rest =
    error && isRecord(error.backendDegradation)
      ? error.backendDegradation
      : undefined;
  const graphQLError = readFirstGraphQLError(body);
  const extensions =
    graphQLError && isRecord(graphQLError.extensions)
      ? graphQLError.extensions
      : undefined;
  const graphql =
    extensions && isRecord(extensions.backendDegradation)
      ? extensions.backendDegradation
      : undefined;
  const value = rest ?? graphql;
  if (!value) return undefined;
  const code = value.code;
  return typeof code === 'string'
    ? (value as unknown as CommonBackendDegradationEnvelope)
    : undefined;
}

export function readErrorCodeFromBody(body: unknown): string | undefined {
  if (!isRecord(body)) return undefined;
  const error = isRecord(body.error) ? body.error : undefined;
  const graphQLError = readFirstGraphQLError(body);
  const extensions =
    graphQLError && isRecord(graphQLError.extensions)
      ? graphQLError.extensions
      : undefined;
  const code = error?.code ?? extensions?.code;
  return typeof code === 'string' ? code : undefined;
}

function readFirstGraphQLError(
  body: Record<string, unknown>
): Record<string, unknown> | undefined {
  if (!Array.isArray(body.errors)) return undefined;
  return body.errors.find(isRecord);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}
